import type {ICoupon, IVoucher, IVoucherTemplate} from "@torim-app/types";
import {adminUrlBuilder} from "@app/api/utils";

export class PromotionsApi {

  static readonly path = 'promotions';

  // ── Coupons (promo codes) ──

  static async fetchCoupons(): Promise<ICoupon[]> {
    const url = adminUrlBuilder({}, PromotionsApi.path + '/coupons');
    const res = await fetch(url);
    if (!res.ok) throw 'Failed to get coupons';
    return res.json();
  }

  static async createCoupon(body: Partial<ICoupon>): Promise<ICoupon> {
    const url = adminUrlBuilder({}, PromotionsApi.path + '/coupons');
    const res = await fetch(url, {
      method: 'post',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!res.ok) {
      if (res.status === 409) throw 'DUPLICATE';
      throw 'Failed to create coupon';
    }
    return res.json();
  }

  static async updateCoupon(id: string, body: Partial<ICoupon>): Promise<ICoupon> {
    const url = adminUrlBuilder({}, PromotionsApi.path + `/coupons/${id}`);
    const res = await fetch(url, {
      method: 'put',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!res.ok) {
      if (res.status === 409) throw 'DUPLICATE';
      throw 'Failed to update coupon';
    }
    return res.json();
  }

  static async deleteCoupon(id: string): Promise<{ success: boolean }> {
    const url = adminUrlBuilder({}, PromotionsApi.path + `/coupons/${id}`);
    const res = await fetch(url, { method: 'delete' });
    if (!res.ok) throw 'Failed to delete coupon';
    return res.json();
  }

  static async fetchVoucherTemplates(): Promise<IVoucherTemplate[]> {
    const url = adminUrlBuilder({}, PromotionsApi.path + '/voucher-templates');
    const res = await fetch(url);
    if (!res.ok) throw 'Failed to get voucher templates';
    return res.json();
  }

  static async createVoucherTemplate(body: Partial<IVoucherTemplate>): Promise<IVoucherTemplate> {
    const url = adminUrlBuilder({}, PromotionsApi.path + '/voucher-templates');
    const res = await fetch(url, {
      method: 'post',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!res.ok) throw 'Failed to create voucher template';
    return res.json();
  }

  static async updateVoucherTemplate(id: string, body: Partial<IVoucherTemplate>): Promise<IVoucherTemplate> {
    const url = adminUrlBuilder({}, PromotionsApi.path + `/voucher-templates/${id}`);
    const res = await fetch(url, {
      method: 'put',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!res.ok) throw 'Failed to update voucher template';
    return res.json();
  }

  static async deleteVoucherTemplate(id: string): Promise<{ success: boolean }> {
    const url = adminUrlBuilder({}, PromotionsApi.path + `/voucher-templates/${id}`);
    const res = await fetch(url, { method: 'delete' });
    if (!res.ok) throw 'Failed to delete voucher template';
    return res.json();
  }

  static async fetchVoucherById(voucherId: string): Promise<IVoucher> {
    const url = adminUrlBuilder({}, PromotionsApi.path + `/vouchers/${voucherId}`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw "Failed to get voucher";

    return await response.json();
  }

  static async fetchAllVouchers(): Promise<Array<IVoucher & { isPaid: boolean }>> {
    const url = adminUrlBuilder({}, PromotionsApi.path + `/vouchers`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw "Failed to get vouchers";

    return await response.json();
  }

  static async fetchCustomerVouchers(customerId: string): Promise<Array<IVoucher & { isPaid: boolean }>> {
    const url = adminUrlBuilder({}, PromotionsApi.path + `/vouchers/customer/${customerId}`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw "Failed to get customer vouchers";

    return await response.json();
  }

  static async createVoucher(
    body: IVoucher
  ): Promise<{ voucherId: string }> {
    const url = adminUrlBuilder({}, PromotionsApi.path + '/vouchers');
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    if (!response.ok)
      throw `Failed to create vouchers`;

    return response.json();
  }

  static async markVoucherAsUsed(voucherId: string) {
    const url = adminUrlBuilder({}, PromotionsApi.path + `/vouchers/${voucherId}`);
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok) {
      if (response.status === 400)
        throw "USED";
      else if (response.status === 404)
        throw "WRONG"
      else if (response.status === 410)
        throw "EXPIRED"
      else throw "ERROR"
    }
  }

  static async sendVoucher(voucherId: string) {
    const url = adminUrlBuilder({}, PromotionsApi.path + `/vouchers/send/${voucherId}`);
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok) throw "ERROR"
  }
}
